import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  Signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Location, LocationsMap } from '@fcom/core-api/interfaces/location-routecff.interface';
import { DateFormat } from '@fcom/core/utils/date.format';
import { replaceMsgFormat } from '@fcom/core/utils/replaceMsgFormat';
import { LanguageService } from '@fcom/ui-translate';
import { LocationsService } from '@fcom/locations';
import { FlightSummaryComponent, FlightSummaryItem } from '@fcom/ui-components';
import { formatTime } from '@fcom/common/pipes/time.pipe';

export interface FlightSummaryBound {
  departure: { dateTime: string; locationCode: string };
  arrival: { dateTime: string; locationCode: string };
}

@Component({
  selector: 'fin-flight-selection-flight-summary',
  templateUrl: './flight-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightSummaryContainerComponent {
  private languageService = inject(LanguageService);
  private locationsService = inject(LocationsService);

  listStyle = input<'ordered' | 'departure-return'>('departure-return');
  bounds = input.required<FlightSummaryBound[]>();

  clickedBound = output<number>();

  private view = viewChild(FlightSummaryComponent);

  private readonly dateTranslations = this.languageService.translateSignal<Record<string, unknown>>('date');
  private readonly departureLabel = this.languageService.translateSignal('ticketSelection.departure');
  private readonly returnLabel = this.languageService.translateSignal('ticketSelection.return');
  private readonly flightNumberTemplate = this.languageService.translateSignal(
    'multiCity.flightSelection.flightNumber'
  );
  private readonly returnToBoundAriaTemplate = this.languageService.translateSignal(
    'multiCity.flightSelection.returnToBoundAria'
  );

  private readonly locations = toSignal(this.locationsService.retrieveAllLocations(), {
    initialValue: {} as LocationsMap,
  });

  private readonly triggeredCodes = new Set<string>();

  readonly items: Signal<FlightSummaryItem[]> = computed(() => {
    const bounds = this.bounds();
    if (!bounds?.length) {
      return [];
    }

    const dateTranslations = this.dateTranslations();
    const locations = this.locations();

    return bounds.map((bound, index) => {
      const departureLocation = this.locationName(bound.departure.locationCode, locations);
      const arrivalLocation = this.locationName(bound.arrival.locationCode, locations);
      const date = dateTranslations
        ? new DateFormat(dateTranslations).format(bound.departure.dateTime, DateFormat.DATE)
        : '';

      return {
        label: this.label(index),
        date,
        departure: { time: formatTime(bound.departure.dateTime), location: departureLocation },
        arrival: { time: formatTime(bound.arrival.dateTime), location: arrivalLocation },
        ariaLabel: this.ariaLabel(index, departureLocation, arrivalLocation, date),
      };
    });
  });

  constructor() {
    effect(() => {
      const locations = this.locations();
      (this.bounds() ?? [])
        .flatMap((bound) => [bound.departure.locationCode, bound.arrival.locationCode])
        .forEach((locationCode) => {
          if (!locations[locationCode] && !this.triggeredCodes.has(locationCode)) {
            this.locationsService.triggerLocationFetch(locationCode);
            this.triggeredCodes.add(locationCode);
          }
        });
    });
  }

  focusRow(index: number): void {
    this.view()?.focusRow(index);
  }

  private label(index: number): string {
    if (this.listStyle() === 'ordered') {
      return replaceMsgFormat(this.flightNumberTemplate() ?? '', [index + 1]);
    }
    return (index === 0 ? this.departureLabel() : this.returnLabel()) ?? '';
  }

  private locationName(locationCode: string, locations: LocationsMap): string {
    const location: Location = locations[locationCode];
    return location ? location.cityName || location.title || locationCode : locationCode;
  }

  private ariaLabel(index: number, origin: string, destination: string, date: string): string | undefined {
    const template = this.returnToBoundAriaTemplate();
    if (this.listStyle() !== 'ordered' || !template) {
      return undefined;
    }
    return replaceMsgFormat(template, {
      bound: index + 1,
      origin,
      destination,
      date,
    });
  }
}
